/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `ManageEvents` for the Caintra Connect frontend.
 * @module pages/ManageEvents
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/ManageEvents.module.css";

import { generateAssetUrl, truncateText } from "../utils/common.js";
import { AppContext } from "../context/AppContext";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";
import ResultsDropdown from "../components/ResultsDropdown";
import PageSelector from "../components/PageSelector";

import { CircularProgress, Backdrop } from "@mui/material";

import { use, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  getManagedRegistrationEventsData,
  showEventCreatedSuccess,
} from "../logic/events";
import { isPastEvent } from "../utils/pastEventsAccess.js";
import {
  canManageRegistrationEvents,
  canPerformEventListActions,
} from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";

import Caintra_Logo_03 from "../assets/Caintra_Logo_03.png";
import EventBanner from "../assets/EventBanner.png";

import { ReactComponent as TitleIcon } from "../assets/icons/Events/TitleIcon.svg";
import { ReactComponent as LocationIcon } from "../assets/icons/Location_on.svg";
import { ReactComponent as CalendarIcon } from "../assets/icons/Calendar_Today.svg";
import { ReactComponent as SalesIcon } from "../assets/icons/Sales.svg";
import { ReactComponent as GroupIcon } from "../assets/icons/Group.svg";
import { ReactComponent as DeleteIcon } from "../assets/icons/Delete.svg";
import { ReactComponent as EditIcon } from "../assets/icons/Edit.svg";
import { ReactComponent as AddIcon } from "../assets/icons/Events/AddIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../assets/icons/Events/CheckCircleIcon.svg";
import { ReactComponent as MobileEditIcon } from "../assets/icons/Events/MobileEditIcon.svg";

import EventCancellationModal from "../modals/EventCancellationModal/index.jsx";
import AssetImage from "../components/AssetImage";
import { DEFAULT_RESULTS_LIMIT } from "../utils/resultsLimitOptions.js";

const manageEventsFormState = { manageEventsFlow: true };

const ManageEvents = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions, permissions } = use(AppContext);
  const { allowed } = useRequireAccess(canManageRegistrationEvents);

  const [eventsData, setEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [cancelEventModalOpen, setCancelEventModalOpen] = useState(false);
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);

  const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1440;
  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;

  const getDevice = () => {
    if (isSmallMobile) {
      return "smallMobile";
    } else if (isMediumMobile) {
      return "mediumMobile";
    } else if (isLaptop) {
      return "laptop";
    } else if (isTablet) {
      return "tablet";
    } else {
      return "desktop";
    }
  };

  const getSearchBarWidth = (device) => {
    if (device === "laptop") {
      return "82.5%";
    } else if (device === "tablet") {
      return "73%";
    } else if (device === "mediumMobile") {
      return "90%";
    } else if (device === "smallMobile") {
      return "75%";
    } else {
      return "87%";
    }
  };

  useEffect(() => {
    if (!allowed) {
      return;
    }
    const initializeData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!!token && token.trim() !== "undefined") {
          await fetchData();
        }
      } catch (error) {
        logger.error("Error during initial data fetch:", {
          err: sanitizeError(error),
        });
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    initializeData();
  }, [allowed]);

  useEffect(() => {
    if (location.state?.eventCreated) {
      handleShowEventCreatedSuccess();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    if (user === null) {
      return;
    }
    if (!canManageRegistrationEvents(user, permissions)) {
      navigate("/events", { replace: true });
    }
  }, [user, permissions, navigate]);

  const fetchData = async () => {
    try {
      const events = await getManagedRegistrationEventsData(
        localStorage.getItem("userId"),
        changePermissions,
        navigate,
        setUser,
      );
      setEventsData(events);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const refreshEvents = async () => {
    try {
      const events = await getManagedRegistrationEventsData(
        localStorage.getItem("userId"),
        changePermissions,
        navigate,
        setUser,
      );
      setEventsData(events);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
      setEventsData([]);
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleResultsLimitChange = (selectedOption) => {
    setResultsLimit(selectedOption.value);
    setCurrentPage(1);
  };

  /**
   * Gets the appropriate event cost based on the user's membership type.
   * Returns member pricing for user types 1, 2, 3, 5 and non-member pricing for type 4.
   * Falls back to default cost if specific pricing is not available.
   * @returns {number} The event cost in MXN
   */
  const getEventCost = (event) => {
    if (user?.tipoDeUsuario === "4") {
      return event.costoNoSocios || event.costo || 0;
    } else if (["1", "2", "3", "5"].includes(user?.tipoDeUsuario)) {
      return event.costoSocios || event.costo || 0;
    } else {
      return 0;
    }
  };

  const filteredEventsList = eventsData?.filter((event) =>
    event.nombre
      ? event.nombre.toLowerCase().includes(searchQuery.toLowerCase())
      : false,
  );

  const handlePageChange = (direction) => {
    const totalPages = Math.ceil(filteredEventsList.length / resultsLimit);
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    } else {
      // Unsupported direction or already at bounds. No action required.
    }
  };

  const paginatedEventsList = filteredEventsList.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );

  const handleEventCancellation = (event) => {
    setSelectedEvent(event);
    setCancelEventModalOpen(true);
  };

  const handleShowEventCreatedSuccess = () => {
    showEventCreatedSuccess(setShowSuccessMessage, setIsSlidingUp);
  };

  const canManage = canPerformEventListActions(
    user,
    permissions,
    canManageRegistrationEvents,
  );

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>{t("pages.events.header")}</h1>
          </div>
          <div className={styles.searchSection}>
            <SearchBar
              placeholder={
                isSmallMobile || isMediumMobile
                  ? t("pages.events.mobileSearchText")
                  : t("pages.events.searchText")
              }
              value={searchQuery}
              onChange={handleSearch}
              width={getSearchBarWidth(getDevice())}
            />
            {canManage && (
              <div
                className={styles.newRequirementButton}
                onClick={() =>
                  navigate("/addEvent", { state: manageEventsFormState })
                }
              >
                <AddIcon />
                {!isSmallMobile && !isMediumMobile ? (
                  <span>{t("pages.events.createEvent")}</span>
                ) : (
                  ""
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.eventsList}>
        {paginatedEventsList.length == 0 && (
          <div className={styles.noResults}>
            <p>{t("pages.events.noResults")}</p>
          </div>
        )}
        {paginatedEventsList.length > 0 &&
          paginatedEventsList.map((event) => (
            <div key={event._id} className={styles.eventCard}>
              <AssetImage
                className={styles.eventImage}
                src={
                  event.imagenBanner
                    ? generateAssetUrl(event.imagenBanner)
                    : EventBanner
                }
                alt={t("common.bannerAlt")}
              />
              <div className={styles.eventInfo}>
                <div className={styles.eventHeader}>
                  <div className={styles.eventHeaderTitle}>
                    <div className={styles.messageItemAvatar}>
                      <img
                        className={styles.avatar}
                        src={Caintra_Logo_03}
                        alt={t("common.avatarAlt")}
                      />
                    </div>
                    <span className={styles.eventName}>{event.nombre}</span>
                  </div>
                  {canManage && !isPastEvent(event) && (
                    <div className={styles.eventHeaderActions}>
                      <div
                        className={styles.buttonContainer}
                        onClick={() =>
                          navigate(`/eventEdit/${event._id}`, {
                            state: manageEventsFormState,
                          })
                        }
                      >
                        {isSmallMobile || isMediumMobile ? (
                          <MobileEditIcon />
                        ) : (
                          <EditIcon className={styles.editButtonIcon} />
                        )}
                        <button className={styles.editButton}>
                          {t("pages.events.editEvent")}
                        </button>
                      </div>
                      <div
                        className={styles.buttonContainer}
                        onClick={() => handleEventCancellation(event)}
                      >
                        <DeleteIcon className={styles.deleteButtonIcon} />
                        <button className={styles.deleteButton}>
                          {t("pages.events.cancelEvent")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.eventDetails}>
                  <div className={styles.registrationEventDate}>
                    <span className={styles.dayOfWeek}>
                      {dayjs(event.fechaEvento).utc().format("dddd")}
                    </span>
                    <span className={styles.day}>
                      {dayjs(event.fechaEvento).utc().format("DD")}
                    </span>
                    <span className={styles.monthAndYear}>
                      {dayjs(event.fechaEvento).utc().format("MMMM")}
                      {", "}
                      {dayjs(event.fechaEvento).utc().format("YYYY")}
                    </span>
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.eventDescriptionContainer}>
                    <span className={styles.eventDescription}>
                      {truncateText(event.descripcion, 50)}
                    </span>
                  </div>
                </div>
                <div className={styles.eventAdditionalDetails}>
                  {getEventCost(event) > 0 && (
                    <div className={styles.eventColumn}>
                      <div className={styles.eventColumnItem}>
                        <SalesIcon className={styles.salesIcon} />
                        <label className={styles.eventLabel}>
                          {t("pages.events.cost")}
                        </label>
                      </div>
                      <span className={styles.eventValue}>
                        {"$ " + getEventCost(event) + " MXN"}
                      </span>
                    </div>
                  )}
                  {event.cupoMaximo && (
                    <div className={styles.eventColumn}>
                      <div className={styles.eventColumnItem}>
                        <GroupIcon className={styles.salesIcon} />
                        <label className={styles.eventLabel}>
                          {t("pages.events.maximumCapacity")}
                        </label>
                      </div>
                      <span className={styles.eventValue}>
                        {event.cupoMaximo + " " + t("pages.events.people")}
                      </span>
                    </div>
                  )}
                  {event.fechaLimiteRegistro && (
                    <div className={styles.eventColumn}>
                      <div className={styles.eventColumnItem}>
                        <CalendarIcon className={styles.salesIcon} />
                        <label className={styles.eventLabel}>
                          {t("pages.events.dueDateRegistration")}
                        </label>
                      </div>
                      <span className={styles.eventValue}>
                        {dayjs(event.fechaLimiteRegistro).utc().format("LLL")}
                      </span>
                    </div>
                  )}
                  {event.ubicacion && (
                    <div className={styles.eventColumn}>
                      <div className={styles.eventColumnItem}>
                        <LocationIcon className={styles.salesIcon} />
                        <label className={styles.eventLabel}>
                          {t("pages.events.location")}
                        </label>
                      </div>
                      <span className={styles.eventValue}>
                        {event.ubicacion}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>
      <div className={styles.footer}>
        <ResultsDropdown onResultsLimitChange={handleResultsLimitChange} />

        <PageSelector
          currentPage={currentPage}
          totalPages={Math.ceil(filteredEventsList.length / resultsLimit)}
          onPageChange={handlePageChange}
        />
      </div>
      <EventCancellationModal
        open={cancelEventModalOpen}
        onCancel={() => setCancelEventModalOpen(false)}
        event={selectedEvent}
        onSuccess={() => {
          setCancelEventModalOpen(false);
          setSelectedEvent(null);
          refreshEvents();
        }}
      />
      {showSuccessMessage && (
        <div
          className={`${styles.successMessage} ${
            isSlidingUp ? styles.slideUp : ""
          }`}
        >
          <CheckCircleIcon className={styles.successMessageIcon} />
          {t("pages.events.creationMessage")}
        </div>
      )}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default ManageEvents;
